import { supabase } from '../../lib/supabase';

const AVATAR_BUCKET = 'avatars';

/**
 * The avatars bucket is public (see the migration comment on
 * `profiles.avatar_path`), so the URL builds synchronously — the shell paints
 * the sidebar on first render and cannot wait on a signed-URL round trip.
 *
 * The stored value is a PATH. Storing the URL instead would bake the project
 * host into every row, which is fine right up until the day it is not.
 */
export function avatarUrl(avatarPath: string | null | undefined): string | null {
  if (avatarPath === null || avatarPath === undefined || avatarPath === '') return null;
  return supabase.storage.from(AVATAR_BUCKET).getPublicUrl(avatarPath).data.publicUrl;
}

const AVATAR_MIME_EXT: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
};

/** 2 MB, matching the bucket's own limit — refused here so the user gets a
 *  sentence instead of a storage error code. */
const MAX_BYTES = 2 * 1024 * 1024;

/**
 * One photo per person at a fixed path, upserted on re-upload — the same
 * decision institute-branding made, and for the same reason: there is no
 * history worth keeping, and a fixed path means `profiles.avatar_path` never
 * has to change once it is set.
 *
 * The extension still varies by type, so replacing a PNG with a JPEG leaves the
 * old object behind. That is deliberate: deleting first would mean a window
 * where the person has no photo if the upload then fails.
 */
export async function uploadAvatar(userId: string, file: File): Promise<string> {
  const ext = AVATAR_MIME_EXT[file.type];
  if (ext === undefined) {
    throw new Error('Your photo must be a PNG, JPEG or WebP image.');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('Your photo must be 2 MB or smaller.');
  }
  const path = `${userId}/avatar.${ext}`;
  const { error } = await supabase.storage
    .from(AVATAR_BUCKET)
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error !== null) throw new Error(error.message);
  return path;
}
